import type { ErrorCode, FieldIssue } from "@thyroaid/shared";

/** Failures detected by the client itself, before or instead of a server error envelope. */
export type ClientErrorCode =
  | "NETWORK_ERROR" // request never got a response (offline, DNS, CORS, connection reset)
  | "TIMEOUT" // no response within the client timeout
  | "INVALID_RESPONSE" // response wasn't the /api/v1 envelope, or data failed its parser
  | "HTTP_ERROR"; // non-2xx without an error envelope (e.g. a proxy's HTML 502 page)

export type ApiErrorCode = ErrorCode | ClientErrorCode;

interface ApiErrorInit {
  code: ApiErrorCode;
  message: string;
  /** HTTP status; undefined when no response arrived (network error, timeout). */
  status?: number | undefined;
  /** Server request ID (from the envelope) or the client-generated one. Quote it to support. */
  requestId?: string | undefined;
  issues?: readonly FieldIssue[] | undefined;
  cause?: unknown;
}

/**
 * Every failed API call rejects with ApiError (WEB-009). `status` follows the shape the query
 * retry policy reads: 4xx never retries; 5xx/429 and status-less (network/timeout) do.
 */
export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number | undefined;
  readonly requestId: string | undefined;
  readonly issues: readonly FieldIssue[];

  constructor({ code, message, status, requestId, issues, cause }: ApiErrorInit) {
    super(message, cause === undefined ? undefined : { cause });
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.requestId = requestId;
    this.issues = issues ?? [];
  }
}

const CLIENT_MESSAGES: Readonly<Record<ClientErrorCode, string>> = {
  NETWORK_ERROR: "ThyroAID couldn't reach the server. Check your connection and try again.",
  TIMEOUT: "The server took too long to respond. Try again in a moment.",
  INVALID_RESPONSE:
    "The server sent a response ThyroAID couldn't read. Nothing was changed on this screen. Try again, and contact support if it continues.",
  HTTP_ERROR: "The server is having a problem. Try again in a moment.",
};

export function clientMessage(code: ClientErrorCode): string {
  return CLIENT_MESSAGES[code];
}

/** Display text for any thrown value. Server envelope messages are display-safe by contract. */
export function userMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return "Something went wrong. Try again, and contact support if it continues.";
}
